/**
 * FreeLeadsPanel — Admin view of `/free` concierge-audit lead requests.
 *
 * Every visitor who submits the `/free` form lands here. Status flow:
 *   pending → fulfilled (email sent with PDF attached)
 *   pending → rejected (spam / bad-fit / duplicate)
 * From this table the team can:
 *   • Filter by status / goal
 *   • Search across the row
 *   • Open a fulfilment dialog to upload the finished PDF + optional
 *     note and click "Send fulfilment email" — the PDF is emailed via
 *     Resend and the lead is stamped fulfilled.
 *   • Reject / re-open a lead.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Loader2, RefreshCw, Search, ExternalLink, Users, Copy, CheckCircle2,
  Clock, Ban, Send, Paperclip, X, RotateCcw, Download,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { AdminDateFilter } from './AdminDateFilter';
import { ReferralCell } from './HeroLeadsPanel';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose,
} from '../ui/dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { adminApi, downloadFreeLeadAuditReport } from '../../lib/api';
import { getErrorMessage } from '../../lib/errors';

const GOAL_LABEL = {
  recruiter_visibility: 'Recruiter visibility',
  business_development: 'Business dev.',
  thought_leadership: 'Thought leadership',
};

const STATUS_META = {
  pending:   { label: 'Pending',   icon: Clock,        cls: 'bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300' },
  fulfilled: { label: 'Fulfilled', icon: CheckCircle2, cls: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300' },
  rejected:  { label: 'Rejected',  icon: Ban,          cls: 'bg-slate-100 text-slate-700 dark:bg-slate-500/15 dark:text-slate-300' },
};

function StatChip({ icon: Icon, label, value, tone = 'slate' }) {
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-border bg-background px-4 py-3">
      <span className={`inline-flex h-9 w-9 items-center justify-center rounded-xl bg-${tone}-100 text-${tone}-700 dark:bg-${tone}-500/15 dark:text-${tone}-300`}>
        <Icon size={16} />
      </span>
      <div>
        <p className="text-[10.5px] font-mono-edge tracking-[0.18em] uppercase text-muted-foreground leading-none">{label}</p>
        <p className="mt-1 text-[22px] font-bold text-foreground tabular-nums leading-none">{value}</p>
      </div>
    </div>
  );
}

function FulfilDialog({ lead, open, onOpenChange, onFulfilled }) {
  const [file, setFile] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setFile(null);
      setNote('');
      setBusy(false);
    }
  }, [open]);

  const submit = async () => {
    if (!file) {
      toast.error('Please attach the finished PDF first.');
      return;
    }
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Only PDF files are accepted.');
      return;
    }
    setBusy(true);
    try {
      await adminApi.fulfilFreeLead(lead.id, { file, note });
      toast.success(`Email sent to ${lead.email}. Lead marked fulfilled.`);
      onFulfilled?.();
      onOpenChange?.(false);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Fulfilment failed — please retry.'));
    } finally {
      setBusy(false);
    }
  };

  if (!lead) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange?.(v); }}>
      <DialogContent className="sm:max-w-lg" data-testid="free-fulfil-dialog">
        <DialogHeader>
          <DialogTitle>Send fulfilment email</DialogTitle>
          <DialogDescription>
            Attach the finished audit PDF for <b>{lead.name}</b> (<span className="text-foreground">{lead.email}</span>). We&apos;ll email it right away and mark the lead as fulfilled.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 space-y-4">
          <div className="rounded-lg border border-border bg-muted/40 p-3 text-[12.5px] leading-snug">
            <div><span className="text-muted-foreground">LinkedIn:</span>{' '}
              <a href={lead.linkedin_url} target="_blank" rel="noreferrer" className="text-sky-600 dark:text-sky-400 underline">{lead.linkedin_url}</a>
            </div>
            <div><span className="text-muted-foreground">Goal:</span>{' '}
              {GOAL_LABEL[lead.goal] || lead.goal}
            </div>
            {lead.phone && (
              <div><span className="text-muted-foreground">Phone:</span> {lead.phone}</div>
            )}
          </div>

          <div>
            <label className="text-[12px] font-semibold text-foreground">Finished PDF</label>
            <div className="mt-1.5">
              <input
                ref={fileRef}
                type="file"
                accept="application/pdf"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="hidden"
                data-testid="free-fulfil-file-input"
              />
              <div
                role="button"
                tabIndex={0}
                onClick={() => fileRef.current?.click()}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileRef.current?.click(); }}
                className={`cursor-pointer rounded-xl border-2 border-dashed px-4 py-3 flex items-center justify-between gap-3 transition ${
                  file ? 'border-emerald-500 bg-emerald-50/50 dark:bg-emerald-500/[0.06]' : 'border-border hover:border-sky-500'
                }`}
                data-testid="free-fulfil-dropzone"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <Paperclip size={14} className="text-muted-foreground shrink-0" />
                  <div className="min-w-0">
                    {file ? (
                      <>
                        <p className="text-[13px] font-semibold text-foreground truncate">{file.name}</p>
                        <p className="text-[11px] text-muted-foreground">{(file.size / 1024).toFixed(0)} KB · Click to change</p>
                      </>
                    ) : (
                      <>
                        <p className="text-[13px] font-semibold text-foreground">Choose or drop the finished PDF</p>
                        <p className="text-[11px] text-muted-foreground">PDF only · up to 20 MB</p>
                      </>
                    )}
                  </div>
                </div>
                {file && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setFile(null); if (fileRef.current) fileRef.current.value = ''; }}
                    className="p-1 rounded hover:bg-background text-muted-foreground hover:text-foreground"
                    aria-label="Remove file"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>
          </div>

          <div>
            <label htmlFor="free-fulfil-note" className="text-[12px] font-semibold text-foreground">Optional note to the buyer</label>
            <Textarea
              id="free-fulfil-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Anything you'd like to add above the PDF? (Tone tips, next-step recommendation, upsell nudge…)"
              rows={4}
              maxLength={2000}
              disabled={busy}
              className="mt-1.5"
              data-testid="free-fulfil-note-input"
            />
            <p className="mt-1 text-[10.5px] text-muted-foreground text-right">{note.length}/2000</p>
          </div>
        </div>

        <DialogFooter className="mt-2 gap-2">
          <DialogClose asChild>
            <Button variant="ghost" disabled={busy} data-testid="free-fulfil-cancel">Cancel</Button>
          </DialogClose>
          <Button
            onClick={submit}
            disabled={busy || !file}
            className="gap-1.5"
            data-testid="free-fulfil-send"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {busy ? 'Sending…' : 'Send fulfilment email'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function FreeLeadsPanel() {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ pending: 0, fulfilled: 0, rejected: 0 });
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [goalFilter, setGoalFilter] = useState('all');
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [fulfilLead, setFulfilLead] = useState(null);
  const [rowActionId, setRowActionId] = useState(null);
  const [exporting, setExporting] = useState(false);
  // Track which row is currently building its PPT so we can disable that
  // specific button while the browser downloads the file.
  const [pptExportingId, setPptExportingId] = useState(null);

  const exportRowPpt = useCallback(async (row) => {
    if (pptExportingId) return;
    setPptExportingId(row.id);
    const label = (row.name || row.email || 'lead').split('@')[0];
    const t = toast.loading('Building PPT (score card + peer benchmark + topics + posts recommended)…');
    try {
      await downloadFreeLeadAuditReport(row.id, label, 'full', 'pptx');
      toast.success('PPT downloaded.', { id: t });
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not build PPT'), { id: t, duration: 6500 });
    } finally {
      setPptExportingId(null);
    }
  }, [pptExportingId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (goalFilter !== 'all') params.goal = goalFilter;
      if (dateRange.from) params.date_from = dateRange.from;
      if (dateRange.to) params.date_to = dateRange.to;
      const data = await adminApi.listFreeLeads(params);
      setRows(data?.items || []);
      setStats(data?.stats || { pending: 0, fulfilled: 0, rejected: 0 });
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to load free-giveaway leads'));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, goalFilter, dateRange.from, dateRange.to]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) => [r.linkedin_url, r.email, r.name, r.phone, r.goal, r.ip, r.fulfilment_notes]
      .filter(Boolean)
      .map((v) => String(v).toLowerCase())
      .join(' | ')
      .includes(needle));
  }, [rows, q]);

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied');
    } catch { /* ignore */ }
  };

  const setStatus = async (row, status) => {
    if (rowActionId === row.id) return;
    setRowActionId(row.id);
    try {
      await adminApi.updateFreeLead(row.id, { status });
      toast.success(`Lead marked ${status}.`);
      await load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Update failed'));
    } finally {
      setRowActionId(null);
    }
  };

  const exportCsv = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const params = new URLSearchParams({ limit: '5000' });
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (goalFilter !== 'all') params.set('goal', goalFilter);
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/free-leads/export.csv?${params}`;
      const token = localStorage.getItem('le_token');
      const resp = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error(`Export failed (HTTP ${resp.status})`);
      const blob = await resp.blob();
      const dl = document.createElement('a');
      const filename = (resp.headers.get('content-disposition') || '').match(/filename="?([^"]+)"?/i)?.[1]
        || `blayzrr-free-leads-${Date.now()}.csv`;
      dl.href = URL.createObjectURL(blob);
      dl.download = filename;
      document.body.appendChild(dl);
      dl.click();
      dl.remove();
      URL.revokeObjectURL(dl.href);
      toast.success(`Downloaded ${filename}`);
    } catch (e) {
      toast.error(e?.message || 'CSV export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-free-leads-panel">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatChip icon={Users}       label="Total in view" value={rows.length}  tone="sky" />
        <StatChip icon={Clock}       label="Pending"       value={stats.pending}   tone="amber" />
        <StatChip icon={CheckCircle2} label="Fulfilled"    value={stats.fulfilled} tone="emerald" />
        <StatChip icon={Ban}         label="Rejected"      value={stats.rejected}  tone="slate" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, LinkedIn URL, notes…"
            className="pl-9 h-10"
            data-testid="free-leads-search"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px] h-10" data-testid="free-leads-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="fulfilled">Fulfilled</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
        <Select value={goalFilter} onValueChange={setGoalFilter}>
          <SelectTrigger className="w-[200px] h-10" data-testid="free-leads-goal-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All goals</SelectItem>
            <SelectItem value="recruiter_visibility">Recruiter visibility</SelectItem>
            <SelectItem value="business_development">Business development</SelectItem>
            <SelectItem value="thought_leadership">Thought leadership</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={load} disabled={loading} className="h-10 gap-1.5" data-testid="free-leads-refresh">
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          Refresh
        </Button>
        <Button
          variant="default"
          onClick={exportCsv}
          disabled={exporting || rows.length === 0}
          className="h-10 gap-1.5"
          data-testid="free-leads-export-csv"
        >
          {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
          Export CSV
        </Button>
      </div>

      <div className="mb-3">
        <AdminDateFilter value={dateRange} onChange={setDateRange} testId="free-leads-date-filter" />
      </div>

      <div className="rounded-xl border border-border bg-background overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[220px]">Buyer</TableHead>
              <TableHead className="w-[220px]">LinkedIn</TableHead>
              <TableHead>Goal</TableHead>
              <TableHead>Referral</TableHead>
              <TableHead>Received</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                  <Loader2 size={16} className="inline animate-spin mr-2" /> Loading…
                </TableCell>
              </TableRow>
            )}
            {!loading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-foreground py-8" data-testid="free-leads-empty">
                  No free-giveaway leads match the current filters yet.
                </TableCell>
              </TableRow>
            )}
            {!loading && filtered.map((r) => {
              const url = r.linkedin_url || '';
              const short = url.replace(/^https?:\/\//, '').replace(/\/$/, '');
              const status = r.status || 'pending';
              const meta = STATUS_META[status] || STATUS_META.pending;
              const StatusIcon = meta.icon;
              const busy = rowActionId === r.id;
              return (
                <TableRow key={r.id} data-testid={`free-lead-row-${r.id}`}>
                  <TableCell className="align-top">
                    <p className="text-[13px] font-semibold text-foreground leading-tight">{r.name}</p>
                    <p className="text-[11.5px] text-muted-foreground break-all">{r.email}</p>
                    {r.phone && <p className="text-[11px] text-muted-foreground">{r.phone}</p>}
                  </TableCell>
                  <TableCell className="align-top">
                    <a
                      href={url.startsWith('http') ? url : `https://${url}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 text-sky-600 dark:text-sky-400 font-medium text-[12.5px] hover:underline"
                      title={url}
                    >
                      <span className="max-w-[180px] truncate">{short || '—'}</span>
                      <ExternalLink size={11} />
                    </a>
                    <button
                      type="button"
                      onClick={() => copy(url)}
                      className="ml-2 inline-flex items-center text-muted-foreground hover:text-foreground"
                      aria-label="Copy URL"
                    >
                      <Copy size={11} />
                    </button>
                  </TableCell>
                  <TableCell className="align-top text-[12.5px]">
                    {GOAL_LABEL[r.goal] || r.goal || <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="align-top text-[11.5px] max-w-[200px]">
                    <ReferralCell referral={r.referral} />
                  </TableCell>
                  <TableCell className="align-top text-[12px] text-muted-foreground">
                    {r.created_at ? format(new Date(r.created_at), 'd MMM · HH:mm') : '—'}
                    {r.fulfilled_at && (
                      <p className="text-[11px] text-emerald-700 dark:text-emerald-400 mt-0.5">
                        Sent {format(new Date(r.fulfilled_at), 'd MMM · HH:mm')}
                      </p>
                    )}
                    {r.fulfilment_filename && (
                      <p className="text-[11px] text-muted-foreground truncate max-w-[180px]" title={r.fulfilment_filename}>
                        <Paperclip size={10} className="inline mr-0.5" />{r.fulfilment_filename}
                      </p>
                    )}
                  </TableCell>
                  <TableCell className="align-top">
                    <span className={`inline-flex items-center gap-1 rounded-full ${meta.cls} px-2 py-0.5 text-[11px] font-semibold`}>
                      <StatusIcon size={11} /> {meta.label}
                    </span>
                    {r.fulfilment_notes && (
                      <p className="mt-1 text-[11px] text-muted-foreground max-w-[220px] line-clamp-2" title={r.fulfilment_notes}>
                        &ldquo;{r.fulfilment_notes}&rdquo;
                      </p>
                    )}
                  </TableCell>
                  <TableCell className="align-top text-right">
                    <div className="flex flex-col gap-1.5 items-end">
                      {status !== 'fulfilled' && (
                        <Button
                          size="sm"
                          onClick={() => setFulfilLead(r)}
                          disabled={busy}
                          className="h-8 gap-1.5"
                          data-testid={`free-lead-fulfil-${r.id}`}
                        >
                          <Send size={12} /> Fulfil &amp; send
                        </Button>
                      )}
                      {status === 'pending' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setStatus(r, 'rejected')}
                          disabled={busy}
                          className="h-7 gap-1 text-[11.5px]"
                          data-testid={`free-lead-reject-${r.id}`}
                        >
                          {busy ? <Loader2 size={11} className="animate-spin" /> : <Ban size={11} />}
                          Reject
                        </Button>
                      )}
                      {status === 'rejected' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setStatus(r, 'pending')}
                          disabled={busy}
                          className="h-7 gap-1 text-[11.5px]"
                          data-testid={`free-lead-reopen-${r.id}`}
                        >
                          {busy ? <Loader2 size={11} className="animate-spin" /> : <RotateCcw size={11} />}
                          Re-open
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => exportRowPpt(r)}
                        disabled={pptExportingId === r.id}
                        className="h-7 gap-1 text-[11.5px]"
                        data-testid={`free-lead-export-ppt-${r.id}`}
                        title="Score card + peer benchmark + topics + posts recommended"
                      >
                        {pptExportingId === r.id
                          ? <Loader2 size={11} className="animate-spin" />
                          : <Download size={11} />}
                        Export → PPT
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <FulfilDialog
        lead={fulfilLead}
        open={!!fulfilLead}
        onOpenChange={(v) => { if (!v) setFulfilLead(null); }}
        onFulfilled={load}
      />
    </div>
  );
}

export default FreeLeadsPanel;
